import React, { useEffect, useState } from "react";
import { Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { useSelector } from "react-redux";
import WizardLayout from "../../../../components/common/formkit/WizardLayout";
import MediaUploader from "../../../../components/common/formkit/MediaUploader";
import ReviewCard from "../../../../components/common/formkit/ReviewCard";
import { SelectField, FieldShell, Section } from "../../../../components/common/formkit/fields";
import { useUnsavedChangesGuard, useExitWizard } from "../../../../components/common/formkit/formHelpers";

const MAX_DESCRIPTION_LENGTH = 2000;
const POST_MEDIA_MAX_MB = 50; // mobile: "JPEG, PNG, and mp4 up to 50 MB"

const validationSchema = Yup.object({
  service_id: Yup.string().required("Service is required"),
  state: Yup.string().required("State is required"),
  city: Yup.string().required("City is required"),
  description: Yup.string()
    .required("Description is required")
    .max(MAX_DESCRIPTION_LENGTH, "Description too long"),
});

// Fields validated before leaving each step
const STEP_FIELDS = {
  details: ["service_id", "state", "city"],
  content: ["description"],
};

const toPlainText = (html) => String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const toOptions = (list) => (list || []).map((item) => ({ value: item.id, label: item.name }));

/** City dropdown: reloads whenever the selected state changes. */
const CityField = () => {
  const { values, setFieldValue } = useFormikContext();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const user = useSelector((state) => state.auth.user);
  const token = user?.refreshToken || user?.token;
  const [cities, setCities] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!values.state) {
        setCities([]);
        return;
      }
      try {
        // Same page size as the mobile app so the full city list is available
        const res = await axios.post(
          `${baseUrl}/admin/cities/${values.state}`,
          { page_number: 1, page_size: 1000, order_by: "ASC" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setCities(res.data.cities || []);
      } catch {
        if (!cancelled) setCities([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [values.state, baseUrl, token]);

  return (
    <SelectField
      name="city"
      label="City"
      required
      disabled={!values.state}
      helper={!values.state ? "Select a state first" : undefined}
      options={toOptions(cities)}
      onSelect={(value, option) => setFieldValue("city_name", option.label)}
    />
  );
};

const PostWizardBody = ({ mode, services, states, media, onSubmit, isSubmitting }) => {
  const formik = useFormikContext();
  const { values, errors, touched, setFieldValue, setTouched, validateForm } = formik;
  const isEdit = mode === "edit";

  const steps = [
    { key: "details", title: "Details", description: "Service and location" },
    { key: "content", title: "Content", description: "Describe your post" },
    ...(!isEdit && media ? [{ key: "media", title: "Media", description: "Photos, videos, documents" }] : []),
    { key: "review", title: "Review & Submit", description: "Confirm and post" },
  ];

  const [currentKey, setCurrentKey] = useState("details");
  const [reachedKeys, setReachedKeys] = useState([]);

  const mediaCount = media ? media.images.length + media.videos.length + media.attachments.length : 0;
  useUnsavedChangesGuard((formik.dirty || mediaCount > 0) && !isSubmitting);
  const exitWizard = useExitWizard();

  const index = steps.findIndex((s) => s.key === currentKey);

  const handleNext = async () => {
    if (currentKey === "review") {
      onSubmit(values);
      return;
    }
    const fields = STEP_FIELDS[currentKey] || [];
    if (fields.length) {
      const allErrors = await validateForm();
      const stepErrors = fields.filter((f) => allErrors[f]);
      setTouched({ ...touched, ...Object.fromEntries(fields.map((f) => [f, true])) }, false);
      if (stepErrors.length) {
        setTimeout(() => {
          const el = document.querySelector('.fk-card [aria-invalid="true"]');
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            (el.matches("button, input") ? el : el.querySelector("button, input"))?.focus({ preventScroll: true });
          }
        }, 0);
        return;
      }
    }
    setReachedKeys((prev) => (prev.includes(currentKey) ? prev : [...prev, currentKey]));
    setCurrentKey(steps[index + 1].key);
  };

  const handleBack = () => index > 0 && setCurrentKey(steps[index - 1].key);

  // ---- media helpers (create only) ----
  const mediaItems = () => {
    if (!media) return [];
    const build = (list, kind, urlOf, nameOf) =>
      list.map((m, i) => ({
        id: `${kind}-${i}-${urlOf(m)}`,
        kind,
        url: urlOf(m),
        name: nameOf(m),
        index: i,
        isLast: i === list.length - 1,
      }));
    return [
      ...build(media.images, "image", (m) => m.image_url, (m) => m.imagePath),
      ...build(media.videos, "video", (m) => m.image_url, (m) => m.imagePath),
      ...build(media.attachments, "pdf", (m) => m.attachment_url || m.url, (m) => m.attachmentPath || m.name),
    ];
  };
  const mediaSetter = { image: media?.setImages, video: media?.setVideos, pdf: media?.setAttachments };

  const handleUploaded = ({ media: uploaded, attachments }) => {
    media.setImages((prev) => [...prev, ...uploaded.filter((m) => !m.imagePath.endsWith(".mp4"))]);
    media.setVideos((prev) => [...prev, ...uploaded.filter((m) => m.imagePath.endsWith(".mp4"))]);
    media.setAttachments((prev) => [...prev, ...attachments]);
  };
  const handleRemove = (item) => mediaSetter[item.kind]((prev) => prev.filter((_, i) => i !== item.index));
  const handleMove = (item, direction) =>
    mediaSetter[item.kind]((prev) => {
      const list = [...prev];
      const target = item.index + direction;
      if (target < 0 || target >= list.length) return prev;
      [list[item.index], list[target]] = [list[target], list[item.index]];
      return list;
    });

  const renderStep = () => {
    if (currentKey === "details") {
      return (
        <Section title="Service & location" hint="Tell people what your post is about and where.">
          <div className="fk-grid fk-grid--3">
            <SelectField
              name="service_id"
              label="Service"
              required
              options={toOptions(services)}
              onSelect={(value, option) => setFieldValue("service", option.label)}
            />
            <SelectField
              name="state"
              label="State"
              required
              options={toOptions(states)}
              onSelect={(value, option) => {
                setFieldValue("state_name", option.label);
                setFieldValue("city", "");
                setFieldValue("city_name", "");
              }}
            />
            <CityField />
          </div>
        </Section>
      );
    }
    if (currentKey === "content") {
      const over = values.description.length > MAX_DESCRIPTION_LENGTH;
      return (
        <Section title="Write your post" hint="Describe your service, experience, or what you're offering.">
          <FieldShell id="description" label="Description" required error={touched.description && errors.description}>
            <div className="fk-quill" aria-invalid={touched.description && errors.description ? "true" : undefined}>
              <ReactQuill
                theme="snow"
                value={values.description}
                onChange={(value) => setFieldValue("description", value)}
                onBlur={() => setTouched({ ...touched, description: true }, false)}
              />
            </div>
            <div className={`fk-counter ${over ? "fk-counter--over" : ""}`}>
              <span>
                {values.description.length}/{MAX_DESCRIPTION_LENGTH} characters
              </span>
              {over && <span>Description too long</span>}
            </div>
          </FieldShell>
        </Section>
      );
    }
    if (currentKey === "media") {
      return (
        <Section title="Media" hint="Optional. Add images, videos or PDFs to your post.">
          <MediaUploader
            items={mediaItems()}
            onUploaded={handleUploaded}
            onRemove={handleRemove}
            onMove={handleMove}
            maxSizeMb={POST_MEDIA_MAX_MB}
          />
        </Section>
      );
    }
    const items = mediaItems();
    return (
      <div className="fk-review-list">
        <div className="fk-section__head" style={{ margin: 0 }}>
          <h2 className="fk-section__title">{isEdit ? "Review your changes" : "Review your post"}</h2>
          <p className="fk-section__hint">Check everything below, then {isEdit ? "save" : "submit"}.</p>
        </div>
        <ReviewCard
          title="Details"
          onEdit={() => setCurrentKey("details")}
          rows={[
            { label: "Service", value: values.service },
            { label: "State", value: values.state_name },
            { label: "City", value: values.city_name },
          ]}
        />
        <ReviewCard
          title="Content"
          onEdit={() => setCurrentKey("content")}
          rows={[{ label: "Description", value: toPlainText(values.description), full: true }]}
        />
        {media && (
          <ReviewCard title="Media" onEdit={() => setCurrentKey("media")} rows={[{ label: "Files", value: items.length }]}>
            {items.some((m) => m.kind === "image") && (
              <div className="fk-media-grid">
                {items
                  .filter((m) => m.kind === "image")
                  .slice(0, 6)
                  .map((m) => (
                    <div className="fk-media" key={m.id}>
                      <img className="fk-media__thumb" src={m.url} alt={m.name || "Image"} />
                    </div>
                  ))}
              </div>
            )}
          </ReviewCard>
        )}
      </div>
    );
  };

  const isLast = currentKey === "review";

  return (
    <WizardLayout
      title={isEdit ? "Edit Post" : "Create New Post"}
      subtitle={
        isEdit
          ? "Update your post details and information below."
          : "Fill in the service details and location below to share your post with the community."
      }
      steps={steps}
      currentKey={currentKey}
      reachedKeys={reachedKeys}
      onStepClick={setCurrentKey}
      onBack={handleBack}
      onExit={exitWizard}
      onNext={handleNext}
      isLast={isLast}
      nextLabel={isLast ? (isEdit ? "Save Changes" : "Submit Post") : "Next"}
      submitting={isSubmitting}
    >
      {renderStep()}
    </WizardLayout>
  );
};

/**
 * Shared multi-step post form for Create Post and Edit Post.
 * media (create only): { images, videos, attachments, setImages, setVideos, setAttachments }
 */
const PostWizard = ({ mode = "create", initialValues, services, states, media, onSubmit, isSubmitting }) => (
  <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={() => {}}>
    <PostWizardBody
      mode={mode}
      services={services}
      states={states}
      media={media}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
    />
  </Formik>
);

export default PostWizard;
